function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { Quill } from "../patches/PatchedQuill";
export const supportedFormats = ['bold', 'italic', 'link', 'strike', 'underline', 'header', 'indent', 'list'];

/** Rich text is always saved as a stringified object. This is the object interface once parsed. */

export function extractQuillDelta(maybeRichText) {
  try {
    const parsed = JSON.parse(maybeRichText);
    if (parsed.type === 'quill-delta' && 'value' in parsed) {
      return parsed;
    }
    return null;
  } catch (error) {
    return null;
  }
}
export function isEmptyRichText(richText) {
  return richText.value.ops.length === 0 || richText.value.ops.length === 1 && richText.value.ops[0].insert.replaceAll('\\n', '').replaceAll('\n', '').trim() === '';
}
export function isEmptyRichTextString(richTextString) {
  if (!richTextString) {
    return true;
  }
  const parsed = extractQuillDelta(richTextString);
  if (!parsed) {
    return true;
  }
  return isEmptyRichText(parsed);
}
export function normalizeDescription(description) {
  return isEmptyRichText(description) ? '' : JSON.stringify(description);
}
export function convertStrToRichText(maybeHTML) {
  const isHTML = maybeHTML.match(/(<([^>]+)>)/gi) != null;
  if (!isHTML) {
    return maybeHTML;
  } else {
    const containerEl = document.createElement('div');
    containerEl.style.height = '0';
    containerEl.style.visibility = 'hidden';
    document.body.appendChild(containerEl);
    const quill = new Quill(containerEl, {
      formats: supportedFormats
    });
    quill.clipboard.dangerouslyPasteHTML(maybeHTML);
    const pastedDelta = quill.getContents();
    document.body.removeChild(containerEl);
    const richTextString = JSON.stringify(makeParsedRichText(pastedDelta));
    return richTextString.replace(/<([^>]+)>/g, '&lt;$1&gt;');
  }
}
export function convertHTMLToRichText(html) {
  // Check if input is HTML
  const isHTML = /<\/?[a-z][\s\S]*>/i.test(html);
  if (!isHTML) {
    return makeParsedRichText({
      ops: [{
        insert: html
      }]
    });
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  let ops = [];
  function walk(node) {
    let inheritedAttrs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    // TEXT NODE
    if (node.nodeType === Node.TEXT_NODE) {
      var _node$textContent;
      const text = (_node$textContent = node.textContent) !== null && _node$textContent !== void 0 ? _node$textContent : '';
      if (text.trim().length > 0) {
        ops.push({
          insert: text,
          attributes: inheritedAttrs
        });
      }
      return;
    }

    // ELEMENT NODE
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node;
    const attrs = _objectSpread({}, inheritedAttrs);
    const tag = el.tagName;

    // ---------- STRONG / B (standalone title line, unless inside list item) ----------
    if (tag === 'STRONG' || tag === 'B') {
      var _el$parentElement;
      const newAttrs = _objectSpread(_objectSpread({}, attrs), {}, {
        bold: true
      });
      const isInsideListItem = ((_el$parentElement = el.parentElement) === null || _el$parentElement === void 0 ? void 0 : _el$parentElement.tagName) === 'LI';
      el.childNodes.forEach(child => walk(child, newAttrs));
      // Only add newline if NOT inside a list item (treat as inline formatting in lists)
      if (!isInsideListItem) {
        ops.push({
          insert: '\n'
        });
      }
      return;
    }

    // ---------- INLINE FORMATS ----------
    if (tag === 'I' || tag === 'EM') attrs.italic = true;
    if (tag === 'U') attrs.underline = true;
    if (tag === 'S' || tag === 'STRIKE') attrs.strike = true;

    // ---------- LINKS ----------
    if (tag === 'A' && el.hasAttribute('href')) {
      attrs.link = el.getAttribute('href');
    }

    // ---------- HEADINGS (treated like strong titles) ----------
    if (tag === 'H1' || tag === 'H2') {
      // Add 2 newlines before heading if there's already content
      if (ops.length > 0) {
        const lastOp = ops[ops.length - 1];
        // Only add newlines if the last op isn't already a newline
        if (lastOp.insert !== '\n') {
          ops.push({
            insert: '\n'
          });
          ops.push({
            insert: '\n'
          });
        } else {
          // If last op is a newline, add one more
          ops.push({
            insert: '\n'
          });
        }
      }
      const headerValue = tag === 'H1' ? 1 : 2;
      attrs.header = headerValue;
      el.childNodes.forEach(child => walk(child, attrs));
      // Apply header attribute to the newline so Quill treats it as a header block
      ops.push({
        insert: '\n',
        attributes: {
          header: headerValue
        }
      });
      return;
    }

    // ---------- LIST ITEM ----------
    if (tag === 'LI') {
      var _el$parentElement2;
      el.childNodes.forEach(child => walk(child, attrs));
      ops.push({
        insert: '\n',
        attributes: {
          list: ((_el$parentElement2 = el.parentElement) === null || _el$parentElement2 === void 0 ? void 0 : _el$parentElement2.tagName) === 'OL' ? 'ordered' : 'bullet'
        }
      });
      return;
    }

    // ---------- UL / OL ----------
    if (tag === 'UL' || tag === 'OL') {
      el.childNodes.forEach(child => walk(child, attrs));
      // Add one newline after the list ends to separate from next section
      ops.push({
        insert: '\n'
      });
      return;
    }

    // ---------- PARAGRAPH ----------
    if (tag === 'P' || tag === 'DIV') {
      el.childNodes.forEach(child => walk(child, attrs));
      ops.push({
        insert: '\n'
      });
      return;
    }

    // ---------- DEFAULT ----------
    el.childNodes.forEach(child => walk(child, attrs));
  }

  // Walk the document body
  doc.body.childNodes.forEach(node => walk(node));

  // Clean up excessive newlines and post-list spacing
  const cleanedOps = [];
  let consecutiveNewlines = 0;
  for (let i = 0; i < ops.length; i++) {
    var _op$attributes, _prevOp$attributes;
    const op = ops[i];
    const prevOp = i > 0 ? ops[i - 1] : null;
    const isListNewline = op.insert === '\n' && ((_op$attributes = op.attributes) === null || _op$attributes === void 0 ? void 0 : _op$attributes.list);
    const prevWasListNewline = (prevOp === null || prevOp === void 0 ? void 0 : prevOp.insert) === '\n' && (prevOp === null || prevOp === void 0 || (_prevOp$attributes = prevOp.attributes) === null || _prevOp$attributes === void 0 ? void 0 : _prevOp$attributes.list);

    // Skip regular newlines that come right after list items
    if (op.insert === '\n' && !isListNewline && prevWasListNewline) {
      continue;
    }
    if (op.insert === '\n' && !isListNewline) {
      consecutiveNewlines++;
      if (consecutiveNewlines <= 2) {
        cleanedOps.push(op);
      }
    } else {
      consecutiveNewlines = 0;
      cleanedOps.push(op);
    }
  }
  ops = cleanedOps;

  // Ensure final newline (Quill convention)
  if (ops.length === 0 || ops[ops.length - 1].insert !== '\n') {
    ops.push({
      insert: '\n'
    });
  }
  return makeParsedRichText({
    ops
  });
}
export function makeParsedRichText(value) {
  return {
    type: 'quill-delta',
    value
  };
}
export function convertRichTextToHTML(richTextString) {
  if (!richTextString) {
    return '';
  }
  const parsed = extractQuillDelta(richTextString);
  if (!parsed) {
    return '';
  }
  if (isEmptyRichText(parsed)) {
    return '';
  }
  const containerEl = document.createElement('div');
  containerEl.style.height = '0';
  containerEl.style.visibility = 'hidden';
  document.body.appendChild(containerEl);
  const quill = new Quill(containerEl, {
    formats: supportedFormats
  });
  quill.setContents(parsed.value);
  const html = quill.getSemanticHTML();
  document.body.removeChild(containerEl);
  return html;
}